import React, { useState, useEffect, useRef } from 'react';
import './SplashGate.css';
import { signInWithEmailAndPassword, auth } from '../../firebase';

const SplashGate = ({ onAccessGranted }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);

  // Force video autoplay on mobile browsers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Attempt immediate autoplay
    const attemptAutoplay = () => {
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Video autoplay started');
          })
          .catch(error => {
            console.warn('Video autoplay blocked by browser:', error.message);
          });
      }
    };

    // Try to play immediately
    attemptAutoplay();

    // For mobile browsers that block autoplay until user interaction,
    // set up listeners to play on first interaction
    let hasPlayedAfterInteraction = false;

    const playOnInteraction = () => {
      if (!hasPlayedAfterInteraction && video.paused) {
        hasPlayedAfterInteraction = true;
        attemptAutoplay();
      }
    };

    // Listen for various interaction events
    const events = ['touchstart', 'click', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, playOnInteraction, { once: true, passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, playOnInteraction);
      });
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Attempt to sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Authenticated successfully:', userCredential.user.email);
      
      // Access granted - Firebase auth state will be detected by App.js
      onAccessGranted(userCredential.user);
    } catch (error) {
      console.error('Authentication error:', error.code, error.message);
      
      // User-friendly error messages
      switch (error.code) {
        case 'auth/invalid-email':
          setError('Invalid email address format');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Please contact the administrator for access.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        case 'auth/invalid-credential':
          setError('Invalid credentials. Please check your email and password.');
          break;
        default:
          setError('Authentication failed. Please contact the administrator.');
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="splash-gate-overlay">
      {/* Fullscreen background video */}
      <div className="splash-gate-video-container">
        <video 
          ref={videoRef}
          className="splash-gate-video"
          autoPlay 
          loop 
          muted 
          playsInline
          webkit-playsinline="true"
          preload="auto"
        >
          <source src="/splash-video.mp4" type="video/mp4" />
          <source src="/splash-video.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Transparent login form overlay */}
      <div className="splash-gate-container">
        <div className="splash-gate-content">
          <div className="splash-gate-header">
            <h1>Red Handed</h1>
          </div>

          <form onSubmit={handleSubmit} className="splash-gate-form">
            <div className="splash-gate-input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                autoFocus
                disabled={isLoading}
                required
              />
            </div>

            <div className="splash-gate-input-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className="splash-gate-error">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="splash-gate-button"
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SplashGate;
